import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  assignPieceToFolder,
  bulkAssignPiecesToFolder,
  createArchiveFolder,
  deleteArchiveFolder,
  listArchiveFolders,
  listArchivedPieces,
  renameArchiveFolder,
  reorderArchiveFolders,
  searchArchivePieceContents,
  type ArchiveFolder,
} from "@/lib/composerArchive";

export const UNFILED = "__unfiled__";
export const ALL = "__all__";

export function useArchivePanel(workspaceId: string) {
  const qc = useQueryClient();
  const [activeFolder, setActiveFolder] = useState<string>(UNFILED);
  const [newFolderName, setNewFolderName] = useState("");
  const [renaming, setRenaming] = useState<ArchiveFolder | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleting, setDeleting] = useState<ArchiveFolder | null>(null);
  const [dragPieceId, setDragPieceId] = useState<string | null>(null);
  const [dragFolderId, setDragFolderId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const foldersQuery = useQuery({
    queryKey: ["composer-archive-folders", workspaceId],
    queryFn: () => listArchiveFolders(workspaceId),
    enabled: !!workspaceId,
  });
  const piecesQuery = useQuery({
    queryKey: ["composer-archived-pieces", workspaceId],
    queryFn: () => listArchivedPieces(workspaceId),
    enabled: !!workspaceId,
  });

  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);
  const pieces = useMemo(() => piecesQuery.data ?? [], [piecesQuery.data]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    let unfiled = 0;
    for (const p of pieces) {
      if (p.archive_folder_id) map.set(p.archive_folder_id, (map.get(p.archive_folder_id) ?? 0) + 1);
      else unfiled += 1;
    }
    return { byFolder: map, unfiled, total: pieces.length };
  }, [pieces]);

  const folderScopedPieces = useMemo(() => {
    if (activeFolder === ALL) return pieces;
    if (activeFolder === UNFILED) return pieces.filter((p) => !p.archive_folder_id);
    return pieces.filter((p) => p.archive_folder_id === activeFolder);
  }, [pieces, activeFolder]);

  const allPieceIds = useMemo(() => pieces.map((p) => p.id), [pieces]);

  const searchQuery = useQuery({
    queryKey: ["composer-archive-search", workspaceId, debouncedSearch, allPieceIds.length],
    queryFn: () => searchArchivePieceContents(workspaceId, allPieceIds, debouncedSearch),
    enabled: !!workspaceId && debouncedSearch.length >= 2 && allPieceIds.length > 0,
    staleTime: 30_000,
  });

  const visiblePieces = useMemo(() => {
    if (debouncedSearch.length < 2) return folderScopedPieces;
    const matchSet = searchQuery.data;
    const q = debouncedSearch.toLowerCase();
    return folderScopedPieces.filter((p) => {
      if ((p.title ?? "").toLowerCase().includes(q)) return true;
      if ((p.content_type ?? "").toLowerCase().includes(q)) return true;
      return matchSet?.has(p.id) ?? false;
    });
  }, [folderScopedPieces, debouncedSearch, searchQuery.data]);

  useEffect(() => {
    setSelected((cur) => {
      const next = new Set<string>();
      for (const p of pieces) if (cur.has(p.id)) next.add(p.id);
      return next.size === cur.size ? cur : next;
    });
  }, [pieces]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["composer-archive-folders", workspaceId] });
    qc.invalidateQueries({ queryKey: ["composer-archived-pieces", workspaceId] });
  };

  const createMut = useMutation({
    mutationFn: (name: string) => createArchiveFolder(workspaceId, name),
    onSuccess: (f) => {
      invalidate();
      setNewFolderName("");
      setActiveFolder(f.id);
      toast.success("Folder created");
    },
    onError: (e: Error) => toast.error(e.message ?? "Could not create folder"),
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameArchiveFolder(id, name),
    onSuccess: () => {
      invalidate();
      setRenaming(null);
      toast.success("Folder renamed");
    },
    onError: (e: Error) => toast.error(e.message ?? "Could not rename"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteArchiveFolder(id),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
      setActiveFolder(UNFILED);
      toast.success("Folder deleted (pieces moved to Unfiled)");
    },
    onError: (e: Error) => toast.error(e.message ?? "Could not delete"),
  });

  const assignMut = useMutation({
    mutationFn: ({ pieceId, folderId }: { pieceId: string; folderId: string | null }) =>
      assignPieceToFolder(pieceId, folderId),
    onSuccess: () => {
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message ?? "Could not move piece"),
  });

  const bulkAssignMut = useMutation({
    mutationFn: ({ ids, folderId }: { ids: string[]; folderId: string | null }) =>
      bulkAssignPiecesToFolder(ids, folderId),
    onSuccess: (_d, vars) => {
      invalidate();
      toast.success(`Moved ${vars.ids.length} piece${vars.ids.length === 1 ? "" : "s"}`);
      setSelected(new Set());
    },
    onError: (e: Error) => toast.error(e.message ?? "Could not move pieces"),
  });

  const reorderMut = useMutation({
    mutationFn: (orderedIds: string[]) => reorderArchiveFolders(orderedIds),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message ?? "Could not reorder folders"),
  });

  const handleDropOnFolder = (folderId: string | null) => {
    if (dragPieceId && selected.has(dragPieceId) && selected.size > 1) {
      bulkAssignMut.mutate({ ids: Array.from(selected), folderId });
      setDragPieceId(null);
      return;
    }
    if (!dragPieceId) return;
    assignMut.mutate({ pieceId: dragPieceId, folderId });
    setDragPieceId(null);
  };

  const handleFolderReorderDrop = (targetId: string) => {
    if (!dragFolderId || dragFolderId === targetId) {
      setDragFolderId(null);
      return;
    }
    const ids = folders.map((f) => f.id);
    const from = ids.indexOf(dragFolderId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) {
      setDragFolderId(null);
      return;
    }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragFolderId(null);
    reorderMut.mutate(ids);
  };

  const toggleSelect = (id: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const folderTitle =
    activeFolder === ALL
      ? "All pieces"
      : activeFolder === UNFILED
        ? "Unfiled"
        : folders.find((f) => f.id === activeFolder)?.name ?? "Folder";

  return {
    activeFolder,
    setActiveFolder,
    newFolderName,
    setNewFolderName,
    renaming,
    setRenaming,
    renameValue,
    setRenameValue,
    deleting,
    setDeleting,
    dragPieceId,
    setDragPieceId,
    dragFolderId,
    setDragFolderId,
    search,
    setSearch,
    debouncedSearch,
    selected,
    setSelected,
    folders,
    pieces,
    counts,
    visiblePieces,
    folderTitle,
    piecesQuery,
    createMut,
    renameMut,
    deleteMut,
    bulkAssignMut,
    handleDropOnFolder,
    handleFolderReorderDrop,
    toggleSelect,
  };
}
